import React, { useCallback, useEffect, useState } from 'react';
import Loader from '../../../shared/components/ui/Loader';
import Layout from '../layout/AppLayout';
import Header from '../../../shared/components/layout/Header';
import GridWrapper from '../../../shared/components/layout/GridWrapper';
import {
  createCampaign,
  getCampaignById,
  updateCampaign,
  checkCampaignCreate
} from '../../services/api/implementation/impl';
import FormInput from '../../../shared/components/ui/FormInput';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import Button from '../../../shared/components/ui/Button';
import toast from 'react-hot-toast';
import { useNavigate, useParams } from 'react-router-dom';
import { useSidebar } from '../../../shared/contexts/SidebarContext';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';
import { capitalizeWords } from '../../utils/stringUtils';
import { useProjectsList } from '../../../../contexts/ProjectsContext';

const AddEditCampaign = () => {
  const userId = Cookies.get('userId');
  const { projects, projectsForDropdown, isProjectsLoading } = useProjectsList();
  const schema = yup.object().shape({
    project: yup.string().required('Project is required'),
    camp_name: yup.string().required('Campaign name is required').min(3),
    channel: yup.string().required('Channel is required'),
    budget: yup.string().required('Budget is required'),
    start_date: yup.date().required('Start date is required'),
    end_date: yup
      .date()
      .required('End date is required')
      .min(yup.ref('start_date'), 'End date cannot be before start date'),
    email: yup.string().email().optional(),
    dynamicFields: yup.array().of(
      yup.object().shape({
        key: yup.string(),
        value: yup.string().optional()
      })
    )
  });

  const {
    register,
    handleSubmit,
    setValue,
    control,
    watch,
    reset,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      dynamicFields: []
    }
  });

  const [isloading, setisLoading] = useState(false);
  const [customFieldLabel, setCustomFieldLabel] = useState('');
  const watchQuestion = watch('questions');
  const navigate = useNavigate();
  const watchStartDate = watch('start_date');
  const { id, type } = useParams();
  const { collapsed } = useSidebar();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'dynamicFields'
  });
  const PID = watch('project');

  // Track page view
  useEffect(() => {
    mixpanel.track('Add Edit Campaign Viewed', {
      user_id: userId,
      timestamp: new Date().toISOString()
    });
  }, [userId]);

  const defaultQuestions = [
    { key: 'name', label: 'Name' },
    { key: 'phone_number', label: 'Contact Number' }
  ];

  const allOptions = [
    { value: 'email', label: 'Email' },
    { value: 'address', label: 'Address' },
    { value: 'reference', label: 'Reference' },
    { value: 'occupation', label: 'Occupation' },
    { value: 'budget', label: 'Budget' },
    { value: 'alternate_phone_number', label: 'Alternate Phone Number' },
    { value: 'other', label: 'Other' }
  ];

  const {
    data: campaignLeadData,
    refetch: refetchCampaignData,
    isLoading: isCampaignLoading
  } = useQuery({
    queryKey: ['getCampaignLeads', id],
    queryFn: () => getCampaignById(id),
    enabled: !!id
  });

  const {
    data: checkCampaignCreateData,
    refetch: refetchCheckCampaignCreate,
    isLoading: isCheckCampaignCreateLoading
  } = useQuery({
    queryKey: ['checkCampaignCreate', PID],
    queryFn: () => checkCampaignCreate(PID),
    enabled: !!PID
  });

  useEffect(() => {
    if (id) {
      refetchCampaignData();
    }
  }, [id]);

  useEffect(() => {
    if (type === 'edit') {
      if (!isCampaignLoading && campaignLeadData) {
        // Only set loading to false once data is fetched and form is pre-filled
        setisLoading(false);
      } else {
        // Keep loading true until everything is ready
        setisLoading(true);
      }
    } else {
      // For create mode, control loading with other logic (like form submission)
      reset({
        project: '',
        camp_name: '',
        channel: '',
        budget: '',
        start_date: '',
        end_date: '',
        email: '',
        dynamicFields: []
      });

      setisLoading(false);
    }
  }, [type, isCampaignLoading, campaignLeadData]);

  useEffect(() => {
    const toLocalDateInputFormat = (utcDateStr) => {
      if (!utcDateStr) return '';
      const date = new Date(utcDateStr);
      const offset = date.getTimezoneOffset(); // in minutes
      const localDate = new Date(date.getTime() - offset * 60 * 1000);
      return localDate.toISOString().split('T')[0];
    };

    if (type === 'edit' && campaignLeadData) {
      const campaign = campaignLeadData;

      // Pre-populate main form fields with correct local date formatting
      setValue('camp_name', campaign.name);
      setValue('budget', String(campaign.budget));
      setValue('channel', campaign.channel);
      setValue('start_date', toLocalDateInputFormat(campaign.start_date));
      setValue('end_date', toLocalDateInputFormat(campaign.end_date));
      setValue('project', String(campaign.project_id));

      // Pre-populate dynamic fields
      const dynamicFields = campaign?.questions
        ?.filter((q) => !['name', 'phone_number'].includes(q.name))
        .map((q) => ({
          key: q.name,
          label: q.name, // Use the name as label for existing questions
          value: ''
        }));

      reset((prev) => ({
        ...prev,
        camp_name: campaign.name,
        budget: String(campaign.budget),
        channel: campaign.channel,
        start_date: toLocalDateInputFormat(campaign.start_date),
        end_date: toLocalDateInputFormat(campaign.end_date),
        project: String(campaign.project_id),
        dynamicFields
      }));
    } else {
      reset({
        project: '',
        camp_name: '',
        channel: '',
        budget: '',
        start_date: '',
        end_date: '',
        email: '',
        dynamicFields: []
      });
    }
  }, [type, campaignLeadData, setValue, reset]);

  const usedKeys = [...defaultQuestions.map((dq) => dq.key), ...fields.map((f) => f.key)];
  const availableOptions = allOptions?.filter((opt) => !usedKeys.includes(opt.value));

  useEffect(() => {
    if (watchQuestion && watchQuestion !== '') {
      if (watchQuestion === 'other') {
        // Don't reset the value yet
      } else {
        // Find the option to get both key and label
        const selectedOption = allOptions.find((opt) => opt.value === watchQuestion);
        append({
          key: watchQuestion,
          label: selectedOption ? selectedOption.label : watchQuestion,
          value: ''
        });
        setValue('questions', ''); // Only reset for non-'other'
      }
    }
  }, [watchQuestion, allOptions, append, setValue]);

  const handleAddQuestion = () => {
    if (customFieldLabel.trim() !== '') {
      append({
        key: customFieldLabel.trim(),
        label: customFieldLabel.trim(),
        value: ''
      });
      setCustomFieldLabel('');
      // setValue('questions', '');
    }
  };

  const onSubmitForm = (data) => {
    const questionKeys = [
      ...defaultQuestions.map((q) => q.key.replace(/\s+/g, '_')),
      ...fields.map((f) => f.key.replace(/\s+/g, '_'))
    ];

    const formatDate = (dateStr) => {
      const date = new Date(dateStr);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-indexed
      const year = String(date.getFullYear()).slice(-2); // Get last two digits
      return `${day}-${month}-${year}`;
    };

    const payload = {
      name: data.camp_name,
      budget: Number(data.budget),
      channel: data.channel,
      end_date: data.end_date,
      start_date: data.start_date,
      project_id: Number(data.project),
      url: '', // Add logic to set URL if needed
      template: 'default', // Add logic to set template if needed
      questions: questionKeys
    };
    console.log('Payload to submit:', payload);
    addCampaign(payload);
  };

  const { mutate: addCampaign } = useMutation({
    mutationFn: (payload) => {
      if (type === 'add') {
        return createCampaign(payload);
      } else if (type === 'edit') {
        return updateCampaign(id, payload);
      } else {
        return Promise.reject('Invalid type');
      }
    },
    onMutate: () => {
      setIsSubmitting(true);
      setisLoading(true); // Show loader while submitting
    },
    onSuccess: async () => {
      setIsSubmitting(false);
      setisLoading(false);

      toast.success(
        type === 'edit' ? 'Campaign updated successfully' : 'Campaign created successfully'
      );

      navigate('/builder/marketing/forms');

      reset({
        project: '',
        camp_name: '',
        channel: '',
        budget: '',
        start_date: '',
        end_date: '',
        email: '',
        dynamicFields: []
      });
    },
    onError: (err) => {
      setIsSubmitting(false);
      setisLoading(false);
      console.error('Error saving campaign:', err);
      toast.error(err?.response?.data?.message || err?.message || 'Error saving campaign');
    }
  });

  const handleBudgetChange = useCallback((value) => {
    const cleanedValue = value.replace(/[^0-9.]/g, '').slice(0, 8);
    setValue(`budget`, cleanedValue);
    [setValue];
  });

  useEffect(() => {
    console.log(errors);
  }, [errors]);

  useEffect(() => {
    if (checkCampaignCreateData && !checkCampaignCreateData?.canCreate) {
      toast.error("Can't create campaign without salesguy");
    }
  }, [checkCampaignCreateData]);

  return (
    <>
      <Loader isVisible={isloading || isProjectsLoading} />
      <Layout>
        <div
          className={`fixed z-[10] top-0 transition-all duration-300 ease-in-out ${
            collapsed ? 'left-[76px] right-0' : 'left-64 right-0'
          }`}>
          <Header
            backArrow={false}
            data={{ name: type === 'edit' ? 'Edit Campaign' : 'Create Campaign' }}
          />
        </div>
        <GridWrapper width={100}>
          <form
            onSubmit={handleSubmit(onSubmitForm)}
            className="flex flex-col gap-5 mt-[6rem] bg-white p-7 rounded-lg">
            {/* Campaign Details */}
            <div className="flex justify-between items-center w-full">
              <FormInput
                width={48}
                label={'Project'}
                register={register}
                register_key={'project'}
                isRequired={true}
                type={'dropdown'}
                options={[
                  { value: '', label: 'Select Project', disabled: true, selected: true },

                  ...projectsForDropdown
                ]}
                errors={errors.project}
              />
              <FormInput
                width={48}
                label={'Campaign Name'}
                register_key={'camp_name'}
                minLength={3}
                isRequired={true}
                register={register}
                type={'text'}
                errors={errors.camp_name}
                onType={(text) => setValue('camp_name', capitalizeWords(text))}
              />
            </div>

            <div className="flex justify-between items-center w-full">
              <FormInput
                width={48}
                label="Select Channel"
                register={register}
                register_key="channel"
                type="dropdown"
                options={[
                  { value: '', label: 'Select Channel', disabled: true, selected: true },
                  { value: 'Facebook', label: 'Facebook' },
                  { value: 'Twitter', label: 'Twitter' },
                  { value: 'Google', label: 'Google' },
                  { value: 'WhatsApp', label: 'WhatsApp' },
                  { value: 'Instagram', label: 'Instagram' },
                  { value: 'Pinterest', label: 'Pinterest' },
                  { value: 'Email_Marketing', label: 'Email Marketing' }
                ]}
                errors={errors.channel}
                isRequired
              />
              <FormInput
                width={48}
                label={'Budget'}
                register={register}
                register_key={'budget'}
                isRequired={true}
                type={'text'}
                errors={errors.budget}
                onType={(text) => handleBudgetChange(text)}
              />
            </div>

            <div className="flex justify-between items-center w-full">
              <FormInput
                width={48}
                label="Start Date"
                type="date"
                register={register}
                isRequired={true}
                errors={errors.start_date}
                register_key={'start_date'}
                onType={(text) => {
                  register('start_date', { value: text });
                  setValue('end_date', '');
                }}
              />

              <FormInput
                width={48}
                register={register}
                register_key={'end_date'}
                label={'End Date'}
                type={'date'}
                isRequired={true}
                errors={errors.end_date}
                CustomFeature={{
                  min:
                    watchStartDate && !isNaN(new Date(watchStartDate).getTime())
                      ? new Date(watchStartDate).toISOString().split('T')[0]
                      : ''
                }}
              />
            </div>

            {/* Form Questions */}
            <div className="flex flex-col gap-2 mt-6">
              <span className="text-lg border-b-[1px] border-stone-200 w-full pb-2">
                Form Questions
              </span>

              <div className="flex flex-wrap gap-3 mt-2">
                {defaultQuestions.map((item, index) => (
                  <p
                    key={item.key}
                    className="px-4 py-2 bg-gray-200 border border-gray-300 rounded text-sm flex justify-center items-center">
                    {index + 1}. {item.label}
                  </p>
                ))}
                {fields.map((field, index) => (
                  <div key={field.id} className="flex items-center gap-2">
                    <p className="px-3 py-2 bg-gray-100 border border-gray-300 rounded text-sm flex justify-between items-center gap-3">
                      {defaultQuestions.length + index + 1}. {field.label}
                      <button
                        type="button"
                        onClick={() => remove(index)}
                        className="text-red-600 px-2 py-1">
                        X
                      </button>
                    </p>
                  </div>
                ))}
              </div>

              <div className="flex justify-between items-center w-full flex-wrap gap-4 mt-[1rem]">
                <FormInput
                  width={48}
                  label={'Select Question'}
                  register={register}
                  register_key={'questions'}
                  isRequired={false}
                  type={'dropdown'}
                  options={[{ label: 'Select question', value: '' }, ...availableOptions]}
                />

                {watchQuestion === 'other' && (
                  <div className="flex justify-between items-end w-[48%]">
                    <div className="flex-shrink-0 flex flex-col gap-2 text-gray-700 w-[87%]">
                      <span className="text-gray-600">Enter Question</span>
                      <input
                        type="text"
                        className="w-full p-2 pr-10 border border-gray-300 rounded-md bg-white"
                        placeholder="Custom Question Name"
                        value={customFieldLabel}
                        onChange={(e) => setCustomFieldLabel(capitalizeWords(e.target.value))}
                      />
                    </div>
                    <Button
                      displayType={'secondary'}
                      type="button"
                      className="px-3 py-1 bg-blue-500 text-white rounded"
                      onClick={handleAddQuestion}>
                      Add
                    </Button>
                  </div>
                )}
              </div>
            </div>

            {/* Submit Button */}
            <div className="w-full flex justify-end items-center space-x-4 mt-8">
              <Button type="submit" disabled={isSubmitting || !checkCampaignCreateData?.canCreate}>
                Submit
              </Button>
            </div>
          </form>
        </GridWrapper>
      </Layout>
    </>
  );
};

export default AddEditCampaign;
